import * as Feather from "react-feather";
import React, { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { api, baseURL, customerReqConfig } from "../../src/include/api";
import { useNavigate } from "react-router-dom";
import { calculateProductPrice, getCustomer, ratingAvg1 } from "../include/function";
import { useNotification } from "../Toast/ToastProvider";
import { useCart } from "../../src/components/context/UserCartCountContext";
import {
  addToCartHandler,
  handleWishlist,
  loadCartData,
  loadWishlist,
} from "../helper/cartUtils";
import VariantSelectionModal from "./user/VariantSelectionModal";

// Memoized Arrows
const SampleNextArrow = memo(function SampleNextArrow(props) {
  const { className, onClick } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className} slick-next slick-arrow flex-center rounded-circle border border-gray-100 text-xl text-white transition-1`}
      style={{ zIndex: 2, backgroundColor: "white" }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#FA6800")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
    >
      <i className="ph ph-caret-right" />
    </button>
  );
});

const SamplePrevArrow = memo(function SamplePrevArrow(props) {
  const { className, onClick } = props;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className} slick-prev slick-arrow flex-center rounded-circle border border-gray-100 text-xl hover-text-white transition-1`}
      style={{ zIndex: 2, backgroundColor: "white" }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#FA6800")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
    >
      <i className="ph ph-caret-left" />
    </button>
  );
});

// Main Component
const SimilarProducts = (props) => {
  const { hotDeals = [], hotDealsCategoryId } = props;
  const [addedToCartMap, setAddedToCartMap] = useState({});
  const { fetchCartCount, setTotalWishlistItems } = useCart();
  const [loading, setLoading] = useState(null);
  const navigate = useNavigate();
  const toast = useNotification();
  const customer = getCustomer();
  const [wishlistProductIds, setWishlistProductIds] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [slideList, setSlideList] = useState([]);
  const [savedForLaterData, setSavedForLaterData] = useState({ products: [] });

  
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [modalProduct, setModalProduct] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  
  

  useEffect(() => {
    if (customer?.id) {
      loadCartData(customer.id, () => {}, setAddedToCartMap);
    }
  }, [customer?.id]);

  useEffect(() => {
    if (customer?.id) {
      loadWishlist(
        customer.id,
        setWishlistProductIds,
        setTotalWishlistItems,
        setFetchLoading,
      );
    }
  }, [customer?.id, setTotalWishlistItems]);

  useEffect(() => {
    const handleCartUpdate = () => {
      if (customer?.id) {
        loadCartData(customer.id, () => {}, setAddedToCartMap);
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, [customer?.id, setAddedToCartMap]);

  useEffect(() => {
    const handleWishlistUpdate = () => {
      if (customer?.id) {
        // Re-fetch wishlist and update state
        loadWishlist(
          customer.id,
          setWishlistProductIds,
          setTotalWishlistItems,
          () => {},
        );
      }
    };

    window.addEventListener("wishlistUpdated", handleWishlistUpdate);
    return () =>
      window.removeEventListener("wishlistUpdated", handleWishlistUpdate);
  }, [customer?.id, setWishlistProductIds, setTotalWishlistItems]);

  const handleAddToCart = (product) => {
    // Check if product has variants or qtyAndPrice
    const hasVariants =
      product.productVariant && product.productVariant.length > 0;
    const hasQtyPrice = product.qtyAndPrice && product.qtyAndPrice.length > 0;

    if (hasVariants || hasQtyPrice) {
      setModalProduct(product);
      setShowVariantModal(true);
    } else {
      addToCartHandler({
        product,
        customer,
        setAddedToCartMap,
        setSavedForLaterData,
        fetchCartCount,
        toast,
        navigate,
        setLoading,
        savedForLaterData,
      });
    }
  };

    // Handle confirm from modal
    const handleModalConfirmAdd = (product, selectedOption) => {
      if (modalLoading) return;
  
      setModalLoading(true);
  
      // Prepare product with selected option for addToCartHandler
      const productWithSelection = {
        ...product,
        selectedOption: selectedOption, // Pass selected variant/qty to handler
      };
  
      addToCartHandler({
        product: productWithSelection,
        customer,
        setAddedToCartMap,
        setSavedForLaterData,
        fetchCartCount,
        toast,
        navigate,
        setLoading: setModalLoading, // Use modal loading state
        savedForLaterData,
      }).finally(() => {
        setShowVariantModal(false);
        setModalProduct(null);
        setModalLoading(false);
      });
  };
  
  // Load Slides
  const getSlides = () => {
    setFetchLoading(true);
    api
      .get("/slide_show/", customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setSlideList(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching slides", error);
      })
      .finally(() => {
        setFetchLoading(false);
      });
  };

  useEffect(() => {
    getSlides();
  }, []);

  // Wishlist Handler
  const wishListHandler = async (item) => {
    // Prevent double-clicks for the same product
    if (loading === item.productId) return; // Already processing this item

    setLoading(item.productId);
    await handleWishlist({
      item,
      customer,
      wishlistProductIds,
      setWishlistProductIds,
      setTotalWishlistItems,
      toast,
    });
    setLoading(null);
  };

  const sliderRef = React.useRef(null);

  const settings = {
    dots: false,
    arrows: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 6,
    slidesToScroll: 1,
    initialSlide: 0,
    autoplay: true,
    responsive: [
      { breakpoint: 1599, settings: { slidesToShow: 5 } },
      { breakpoint: 1399, settings: { slidesToShow: 5 } },
      { breakpoint: 1199, settings: { slidesToShow: 2 } },
      { breakpoint: 575, settings: { slidesToShow: 1 } },
    ],
  };


  
  return (
    <section className="top-selling pt-80" id="Deal_of_The_Day">
      <div className="container container-lg">
        <div className="border border-gray-100 p-24 rounded-16">
          <div className="section-heading mb-24">
            <div className="flex-between flex-wrap gap-8">
              <h5 className="text-xl mt-16 fw-bold">Similar Products</h5>

              <div className="flex-align mr-point gap-16">
                <Link
                  to={`/category-banner-products/${hotDealsCategoryId}`}
                  className="text-sm fw-medium text-gray-700 hover-text-main-600 hover-text-decoration-underline"
                >
                  View All
                </Link>
                <button
                  type="button"
                  onClick={() => sliderRef.current?.slickPrev()}
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    border: "1px solid #cbd5e1",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "background-color 0.3s, color 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#FA6800";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.color = "black";
                  }}
                  aria-label="Previous"
                >
                  <i
                    className="ph ph-caret-left text-xl"
                    style={{ margin: 0, lineHeight: 1 }}
                  />
                </button>

                <button
                  type="button"
                  onClick={() => sliderRef.current?.slickNext()}
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    border: "1px solid #cbd5e1",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    transition: "background-color 0.3s, color 0.3s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#FA6800";
                    e.currentTarget.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "white";
                    e.currentTarget.style.color = "black";
                  }}
                  aria-label="Next"
                >
                  <i
                    className="ph ph-caret-right text-xl"
                    style={{ margin: 0, lineHeight: 1 }}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="deals-week-slider arrow-style-two position-relative">
            <Slider {...settings} ref={sliderRef}>
              {hotDeals?.map((item, index) => {
                const priceInfo = calculateProductPrice(item);
                const isAdded = !!addedToCartMap[item.productId];
                  const hasVariants =
                    item.productVariant && item.productVariant.length > 0;
                  const hasQtyPrice =
                    item.qtyAndPrice && item.qtyAndPrice.length > 0;
                  const requiresSelection = hasVariants || hasQtyPrice;
                return (
                  <div
                    key={index}
                    className="product-card h-100 p-16 border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2"
                  >
                    <Link
                      to={`/product-details/${item.productId}`}
                      className="product-card__thumb flex-center rounded-8 bg-gray-50 position-relative"
                    >
                      {item.discountLabel && (
                        <span className="product-card__badge bg-main-600 px-8 py-4 text-sm text-white position-absolute inset-inline-start-0 inset-block-start-0">
                          {item.discountLabel}
                        </span>
                      )}
                      <img
                        src={baseURL + "/" + item.mainImage}
                        alt=""
                        style={{ width: "100px !important" }}
                      />
                    </Link>
                    <div className="product-card__content mt-16">
                      <div className="d-flex justify-content-between align-items-center">
                        {item?.ratingAndReview?.length > 0 ? (
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="badge bg-success d-inline-flex text-center"
                              style={{ fontSize: "13px" }}
                            >
                              {ratingAvg1(item.ratingAndReview)}
                              <i className="ph-fill ph-star ms-1" />
                            </div>
                            <span className="fw-medium text-gray-500">
                              ({item.ratingAndReview.length})
                            </span>
                          </div>
                        ) : (
                          <div />
                        )}
                        <div
                          className="d-flex align-items-center"
                          title={
                            wishlistProductIds.includes(String(item.productId))
                              ? "Already in Wishlist"
                              : "Add to Wishlist"
                          }
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            wishListHandler(item);
                          }}
                          style={{ cursor: "pointer" }}
                          disabled={loading === item.productId}
                        >
                          {loading === item.productId ? (
                            <div
                              className="spinner-border spinner-border-sm text-secondary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : wishlistProductIds.includes(
                              String(item.productId),
                            ) ? (
                            <Feather.Heart
                              fill="red"
                              color="red"
                              size={22}
                              strokeWidth={1.6}
                            />
                          ) : (
                            <Feather.Heart size={22} strokeWidth={1.6} />
                          )}
                        </div>
                      </div>
                      <h6 className="title text-lg fw-semibold mt-12 mb-8">
                        <Link
                          to={`/product-details/${item.productId}`}
                          className="link text-line-2"
                          style={{
                            display: "inline-block",
                            width: "90%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          tabIndex={0}
                        >
                          {item.name}
                        </Link>
                      </h6>

                      <div className="list-product-price my-20">
                        {priceInfo.priceType === "productVariant" ? (
                          <>
                            <div className="text-gray-400 text-md fw-semibold text-decoration-line-through mb-2">
                              {priceInfo.retailPriceDisplay}
                            </div>
                            <div className="text-heading text-md fw-semibold">
                              {priceInfo.sellingPriceDisplay}
                            </div>
                          </>
                        ) : (
                          <>
                            {priceInfo.originalPrice && (
                              <div className="text-gray-400 text-md fw-semibold text-decoration-line-through mb-2">
                                ₹ {priceInfo.originalPrice}
                              </div>
                            )}
                            <span className="text-heading text-md fw-semibold">
                              ₹ {priceInfo.displayPrice}
                              {priceInfo.qtyRange && (
                                <span className="text-gray-500 fw-normal ms-2">
                                  ({priceInfo.qtyRange})
                                </span>
                              )}
                              <span className="text-gray-500 fw-normal">
                                {priceInfo.priceType === "qtyAndPrice"
                                  ? ""
                                  : "/Qty"}
                              </span>
                            </span>
                          </>
                        )}
                      </div>

                      <button
                        className={`product-card__cart btn py-11 px-24 rounded-8 flex-center gap-8 fw-medium ${
                          isAdded
                            ? "bg-main-600 text-white"
                            : "bg-gray-50 text-heading hover-bg-main-600 hover-text-white"
                        }`}
                        onClick={() => handleAddToCart(item)}
                        disabled={loading === item.productId}
                      >
                        {loading === item.productId ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            />
                            <span className="visually-hidden">Loading...</span>{" "}
                            Adding...
                          </>
                        ) : isAdded ? (
                          "\u00A0\u00A0Go To Cart"
                        ) : requiresSelection ? (
                          <>
                            Select Option
                            <i className="ph ph-chevron-right" />
                          </>
                        ) : (
                          <>
                            Add To Cart
                            <i className="ph ph-shopping-cart" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>
        </div>
      </div>
      {/* Variant/Qty Selection Modal*/}
      <VariantSelectionModal
        isOpen={showVariantModal}
        onClose={() => {
          setShowVariantModal(false);
        }}
        product={modalProduct}
        customer={customer}
        onConfirmAdd={handleModalConfirmAdd}
        loading={modalLoading}
      />
    </section>
  );
};

export default SimilarProducts;
